import React, { useEffect, useState } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError, API_BASE } from "../lib/api";
import { fmtCLP, fmtDate } from "../lib/format";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, AlertTriangle, Info, Calendar, Clock,
  Landmark, FileDown, Target, Wallet, Percent, Users, Truck,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, Area, AreaChart,
} from "recharts";

const monthLabel = (yyyymm) => {
  if (!yyyymm) return "";
  const [y, m] = yyyymm.split("-");
  const meses = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
  return `${meses[parseInt(m, 10) - 1]} ${y.slice(2)}`;
};

const CATEGORIA_LABELS = {
  costo_ventas: "Costo de ventas",
  gastos_admin: "Gastos administración",
  gastos_ventas: "Gastos comerciales",
  gastos_fin: "Gastos financieros",
  impuestos: "Impuestos",
  otros_egresos: "Otros egresos",
};

const PIE_COLORS = ["#1B4332", "#2D6A4F", "#7A5028", "#D4A373", "#A94438", "#52796F", "#B5838D"];

export default function Dashboard() {
  const [mes, setMes] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/dashboard/ejecutivo", { params: { mes } })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(extractError(e)))
      .finally(() => setLoading(false));
  }, [mes]);

  const downloadPdf = () => {
    window.open(`${API_BASE}/dashboard/ejecutivo/pdf?mes=${mes}`, "_blank");
  };

  if (loading) return <Page><div className="p-12 text-center text-muted-foreground">Generando informe ejecutivo…</div></Page>;
  if (!data) return <Page><div className="p-12 text-center text-muted-foreground">Sin datos. Crea asientos y facturas primero.</div></Page>;

  const k = data.kpis;
  const c = data.comparativas;
  const i = data.impuestos;
  const r = data.ratios;

  return (
    <Page>
      <PageHeader
        title="Informe Ejecutivo"
        subtitle={`${data.periodo.mes_label} · Resumen contable y financiero del mes`}
        testid="dash-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="month"
              value={mes}
              onChange={(e) => setMes(e.target.value)}
              data-testid="dash-mes"
              className="px-3 py-2 border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm"
            />
            <button onClick={downloadPdf} data-testid="dash-pdf"
              className="inline-flex items-center gap-1.5 border border-destructive text-destructive hover:bg-destructive/10 text-sm font-medium px-3 py-2 rounded-sm">
              <FileDown className="h-4 w-4" /> Descargar PDF
            </button>
          </div>
        }
      />

      {/* ALERTAS */}
      {data.alertas?.length > 0 && (
        <div className="mb-6 space-y-2" data-testid="dash-alertas">
          {data.alertas.map((a, idx) => <AlertaCard key={idx} alerta={a} />)}
        </div>
      )}

      {/* HERO KPIs con comparativas */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6" data-testid="dash-hero">
        <HeroKPI
          label="Ingresos del mes"
          value={fmtCLP(k.ingresos_mes)}
          icon={TrendingUp}
          deltaMes={c.ingresos.delta_mes}
          deltaAno={c.ingresos.delta_ano}
          tone="primary"
        />
        <HeroKPI
          label="Egresos del mes"
          value={fmtCLP(k.egresos_mes)}
          icon={TrendingDown}
          deltaMes={c.egresos.delta_mes}
          deltaAno={c.egresos.delta_ano}
          tone="danger"
          inverseDelta
        />
        <HeroKPI
          label="Utilidad del mes"
          value={fmtCLP(k.utilidad_mes)}
          icon={Target}
          deltaMes={c.utilidad.delta_mes}
          deltaAno={c.utilidad.delta_ano}
          tone={k.utilidad_mes >= 0 ? "primary" : "danger"}
          subtitle={k.margen_neto_mes_pct !== null ? `Margen neto ${k.margen_neto_mes_pct}%` : null}
        />
      </div>

      {/* SECONDARY KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="dash-kpis">
        <SmallKPI icon={Wallet} label="Saldo bancos" value={fmtCLP(k.saldo_bancos)} tone="primary" />
        <SmallKPI icon={Target} label="Utilidad YTD" value={fmtCLP(k.utilidad_ytd)} tone={k.utilidad_ytd >= 0 ? "primary" : "danger"} />
        <SmallKPI icon={Percent} label="Margen YTD" value={k.margen_neto_ytd_pct !== null ? `${k.margen_neto_ytd_pct}%` : "—"} />
        <SmallKPI icon={Landmark} label="Razón corriente" value={r.razon_corriente !== null ? r.razon_corriente.toFixed(2) : "—"}
          tone={r.razon_corriente !== null && r.razon_corriente < 1 ? "danger" : "default"} />
      </div>

      {/* TREND 12M (chart grande) */}
      <div className="bg-card border border-border rounded-md p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="label-caps">Tendencia 12 meses</div>
            <div className="text-sm text-muted-foreground">Ingresos, egresos y utilidad consolidados</div>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart data={data.trend_12m.map((t) => ({ ...t, label: monthLabel(t.mes) }))}>
            <defs>
              <linearGradient id="ing" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#2D6A4F" stopOpacity={0.5} />
                <stop offset="95%" stopColor="#2D6A4F" stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id="egr" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#A94438" stopOpacity={0.4} />
                <stop offset="95%" stopColor="#A94438" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2DFD8" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000000).toFixed(0)}M`} />
            <Tooltip formatter={(v) => fmtCLP(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Area type="monotone" dataKey="ingresos" stroke="#2D6A4F" fill="url(#ing)" name="Ingresos" />
            <Area type="monotone" dataKey="egresos" stroke="#A94438" fill="url(#egr)" name="Egresos" />
            <Line type="monotone" dataKey="utilidad" stroke="#1B4332" strokeWidth={2} dot={{ r: 3 }} name="Utilidad" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* IMPUESTOS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-card border border-border rounded-md p-5" data-testid="dash-iva">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="label-caps">IVA y PPM (F29)</div>
              <div className="text-sm text-muted-foreground">
                {i.periodo_libros_label || data.periodo.mes_label}
                {(i.doc_ventas > 0 || i.doc_compras > 0) && (
                  <> · {i.doc_ventas} venta(s) · {i.doc_compras} compra(s)</>
                )}
              </div>
            </div>
            <Calendar className="h-5 w-5 text-warning-foreground" />
          </div>
          <div className="space-y-2 mb-3">
            <Row label="IVA débito (ventas)" value={fmtCLP(i.iva_debito_mes)} />
            <Row label="IVA crédito (compras)" value={`- ${fmtCLP(i.iva_credito_mes)}`} tone="danger" />
          </div>
          <div className={`rounded-sm p-3 mb-3 ${i.iva_a_pagar_mes > 0 ? "bg-destructive/10 border border-destructive" : "bg-success/10 border border-success"}`}>
            <div className="flex items-center justify-between">
              <span className="font-medium">{i.iva_a_pagar_mes > 0 ? "IVA a pagar" : "Crédito fiscal IVA"}</span>
              <span className={`num text-xl font-bold ${i.iva_a_pagar_mes > 0 ? "text-destructive" : "text-primary"}`}>
                {fmtCLP(Math.abs(i.iva_a_pagar_mes))}
              </span>
            </div>
          </div>
          <div className="space-y-2 mb-3 pt-2 border-t border-border">
            <Row label={`PPM (${i.tasa_ppm_pct}% s/ ingresos libro ventas)`} value={fmtCLP(i.ppm_renta_mes)} />
            <div className="flex justify-between text-sm font-medium pt-1">
              <span className="text-foreground">Total F29 estimado</span>
              <span className="num font-mono text-primary">{fmtCLP(i.f29_total_estimado ?? Math.max(i.iva_a_pagar_mes, 0) + i.ppm_renta_mes)}</span>
            </div>
          </div>
          <div className="text-[11px] text-muted-foreground">
            Datos desde libros de venta/compra · F29 vence el {fmtDate(i.f29_vencimiento)}
          </div>
        </div>

        <div className="bg-card border border-border rounded-md p-5" data-testid="dash-renta">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="label-caps">Impuesto Renta</div>
              <div className="text-sm text-muted-foreground">Proyección anual (27%)</div>
            </div>
            <Target className="h-5 w-5 text-warning-foreground" />
          </div>
          <div className="bg-warning-surface border border-warning rounded-sm p-3">
            <div className="flex items-center justify-between">
              <span className="font-medium">Proyección anual ({i.tasa_renta_pct}%)</span>
              <span className="num text-xl font-bold text-warning-foreground">{fmtCLP(i.impuesto_renta_proyectado_anual)}</span>
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">Estimado al cierre · pago en abril del año siguiente</div>
          </div>
        </div>
      </div>

      {/* TOP CLIENTES / PROVEEDORES + GASTOS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <TopList icon={Users} title="Top 5 Clientes (YTD)" items={data.top_clientes} colorAccent="#1B4332" testid="dash-top-clientes" />
        <TopList icon={Truck} title="Top 5 Proveedores (YTD)" items={data.top_proveedores} colorAccent="#7A5028" testid="dash-top-prov" />
        <div className="bg-card border border-border rounded-md p-4" data-testid="dash-gastos">
          <div className="label-caps mb-2">Distribución de gastos</div>
          {data.gastos_categoria?.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={data.gastos_categoria.map((g) => ({ name: CATEGORIA_LABELS[g.categoria] || g.categoria, value: g.monto }))}
                  dataKey="value" innerRadius={45} outerRadius={80} paddingAngle={2}>
                  {data.gastos_categoria.map((_, idx) => <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmtCLP(v)} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="text-sm text-muted-foreground text-center py-10">Sin gastos registrados</div>}
        </div>
      </div>

      {/* COMPARATIVAS BAR */}
      <div className="bg-card border border-border rounded-md p-4 mb-6">
        <div className="label-caps mb-3">Comparativas vs Mes anterior · Año anterior</div>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={[
            { metric: "Ingresos", actual: c.ingresos.actual, mes_ant: c.ingresos.mes_anterior, ano_ant: c.ingresos.ano_anterior },
            { metric: "Egresos", actual: c.egresos.actual, mes_ant: c.egresos.mes_anterior, ano_ant: c.egresos.ano_anterior },
            { metric: "Utilidad", actual: c.utilidad.actual, mes_ant: c.utilidad.mes_anterior, ano_ant: c.utilidad.ano_anterior },
          ]}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2DFD8" />
            <XAxis dataKey="metric" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000000).toFixed(0)}M`} />
            <Tooltip formatter={(v) => fmtCLP(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="actual" fill="#1B4332" name={`Actual (${data.periodo.mes_label})`} />
            <Bar dataKey="mes_ant" fill="#7A5028" name="Mes anterior" />
            <Bar dataKey="ano_ant" fill="#D4A373" name="Año anterior" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* CUENTAS POR COBRAR/PAGAR */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        <SmallKPI label="Por cobrar (pendiente)" value={fmtCLP(data.cuentas_por_cobrar ?? 0)} tone="warning" />
        <SmallKPI label="Por cobrar (ventas YTD)" value={fmtCLP(data.cuentas_por_cobrar_ytd ?? 0)} />
        <SmallKPI label="Cobrado del mes" value={fmtCLP(data.cuentas_cobradas_mes ?? 0)} tone="primary" />
        <SmallKPI label="Cobrado YTD" value={fmtCLP(data.cuentas_cobradas_ytd ?? 0)} tone="primary" />
        <SmallKPI label="Por pagar (pendiente)" value={fmtCLP(data.cuentas_por_pagar)} tone="warning" />
        <SmallKPI label="Por pagar vencidas" value={fmtCLP(data.cuentas_por_pagar_vencidas)}
          tone={data.cuentas_por_pagar_vencidas > 0 ? "danger" : "default"} />
      </div>
    </Page>
  );
}

// --- Subcomponents ---
function HeroKPI({ label, value, icon: Icon, deltaMes, deltaAno, tone = "default", inverseDelta = false, subtitle }) {
  const tones = { default: "text-foreground", primary: "text-primary", danger: "text-destructive" };
  const deltaColor = (d) => {
    if (d === null || d === undefined) return "text-muted-foreground/70";
    const positive = inverseDelta ? d < 0 : d > 0;
    return positive ? "text-success" : "text-destructive";
  };
  const DeltaIcon = (d) => {
    if (d === null || d === undefined) return null;
    return d > 0 ? <TrendingUp className="h-3 w-3 inline" /> : <TrendingDown className="h-3 w-3 inline" />;
  };
  return (
    <div className="bg-card border border-border rounded-md p-5">
      <div className="flex items-start justify-between mb-3">
        <div className="label-caps">{label}</div>
        <Icon className="h-4 w-4 text-muted-foreground/70" strokeWidth={1.5} />
      </div>
      <div className={`num text-2xl sm:text-3xl tracking-tight font-medium ${tones[tone]}`}>{value}</div>
      {subtitle && <div className="text-xs text-muted-foreground mt-1">{subtitle}</div>}
      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border text-[11px]">
        <span className={deltaColor(deltaMes)}>
          {DeltaIcon(deltaMes)} {deltaMes !== null && deltaMes !== undefined ? `${deltaMes > 0 ? "+" : ""}${deltaMes}%` : "—"}
          <span className="text-muted-foreground ml-1">vs mes ant.</span>
        </span>
        <span className={deltaColor(deltaAno)}>
          {DeltaIcon(deltaAno)} {deltaAno !== null && deltaAno !== undefined ? `${deltaAno > 0 ? "+" : ""}${deltaAno}%` : "—"}
          <span className="text-muted-foreground ml-1">vs año ant.</span>
        </span>
      </div>
    </div>
  );
}

function SmallKPI({ icon: Icon, label, value, tone = "default" }) {
  const tones = { default: "text-foreground", primary: "text-primary", danger: "text-destructive", warning: "text-warning-foreground" };
  return (
    <div className="bg-card border border-border rounded-md p-3 sm:p-4">
      <div className="flex items-center justify-between mb-1">
        <div className="label-caps">{label}</div>
        {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground/70" strokeWidth={1.5} />}
      </div>
      <div className={`num text-base sm:text-lg font-semibold ${tones[tone]}`}>{value}</div>
    </div>
  );
}

function Row({ label, value, tone = "default" }) {
  const tones = { default: "text-foreground", danger: "text-destructive" };
  return (
    <div className="flex justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={`num font-mono ${tones[tone]}`}>{value}</span>
    </div>
  );
}

function TopList({ icon: Icon, title, items, colorAccent, testid }) {
  const maxVal = items?.[0]?.total || 1;
  return (
    <div className="bg-card border border-border rounded-md p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
        <div className="label-caps">{title}</div>
      </div>
      {items?.length > 0 ? (
        <ul className="space-y-2">
          {items.map((it, idx) => {
            const pct = (it.total / maxVal) * 100;
            return (
              <li key={idx} className="text-[12px]">
                <div className="flex justify-between mb-0.5">
                  <span className="truncate flex-1" title={it.nombre}>{idx + 1}. {it.nombre}</span>
                  <span className="font-mono ml-2">{fmtCLP(it.total)}</span>
                </div>
                <div className="h-1 bg-muted rounded-sm overflow-hidden">
                  <div className="h-full" style={{ width: `${pct}%`, backgroundColor: colorAccent }} />
                </div>
              </li>
            );
          })}
        </ul>
      ) : <div className="text-sm text-muted-foreground text-center py-6">Sin datos</div>}
    </div>
  );
}

function AlertaCard({ alerta }) {
  const styles = {
    danger: { bg: "bg-destructive/10", border: "border-destructive", text: "text-destructive", icon: AlertTriangle },
    warning: { bg: "bg-warning-surface", border: "border-warning", text: "text-warning-foreground", icon: AlertTriangle },
    info: { bg: "bg-success/10", border: "border-success", text: "text-primary", icon: Info },
  };
  const ICONS = { "alert-triangle": AlertTriangle, info: Info, calendar: Calendar, clock: Clock, "trending-down": TrendingDown };
  const s = styles[alerta.severidad] || styles.info;
  const IconC = ICONS[alerta.icon] || s.icon;
  return (
    <div className={`flex items-start gap-3 ${s.bg} border-l-4 ${s.border} rounded-sm p-3`} data-testid={`alert-${alerta.tipo}`}>
      <IconC className={`h-5 w-5 ${s.text} shrink-0 mt-0.5`} strokeWidth={1.75} />
      <div className="flex-1 min-w-0">
        <div className={`font-medium text-sm ${s.text}`}>{alerta.titulo}</div>
        <div className="text-[12px] text-foreground mt-0.5">{alerta.mensaje}</div>
        {alerta.fecha_limite && (
          <div className="text-[11px] text-muted-foreground mt-1">
            <Calendar className="h-3 w-3 inline mr-1" />Vence {fmtDate(alerta.fecha_limite)}
          </div>
        )}
      </div>
    </div>
  );
}
